import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { Member } from "@/types";
import { ACCOUNT_ROLE } from "@/constants";
import { formatRelativeTime } from "@/utils";

interface EditMemberDialogProps {
  editingMember: Member | null;
  onClose: () => void;
  editRole: string;
  onEditRoleChange: (role: string) => void;
  editFirstName: string;
  onEditFirstNameChange: (name: string) => void;
  editLastName: string;
  onEditLastNameChange: (name: string) => void;
  editEmail: string;
  onEditEmailChange: (email: string) => void;
  onSave: () => void;
  isUpdating: boolean;
}

export function EditMemberDialog({
  editingMember,
  onClose,
  editRole,
  onEditRoleChange,
  editFirstName,
  onEditFirstNameChange,
  editLastName,
  onEditLastNameChange,
  editEmail,
  onEditEmailChange,
  onSave,
  isUpdating,
}: EditMemberDialogProps) {
  return (
    <Dialog open={!!editingMember} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[420px] bg-card">
        <DialogHeader>
          <DialogTitle>Edit Team Member</DialogTitle>
        </DialogHeader>
        {editingMember && (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">First Name</Label>
              <Input
                value={editFirstName}
                onChange={(e) => onEditFirstNameChange(e.target.value)}
                className="h-9 rounded-lg bg-card"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Last Name</Label>
              <Input
                value={editLastName}
                onChange={(e) => onEditLastNameChange(e.target.value)}
                className="h-9 rounded-lg bg-card"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email Address</Label>
              <Input
                value={editEmail}
                onChange={(e) => onEditEmailChange(e.target.value)}
                className="h-9 rounded-lg bg-card"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Select value={editRole} onValueChange={onEditRoleChange}>
                <SelectTrigger className="h-9 rounded-lg bg-card">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border border-border">
                  <SelectItem value={ACCOUNT_ROLE.ADMIN}>Admin</SelectItem>
                  <SelectItem value={ACCOUNT_ROLE.EDITOR}>Editor</SelectItem>
                  <SelectItem value={ACCOUNT_ROLE.VIEWER}>Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Last Active</Label>
              <p className="text-sm text-muted-foreground">{formatRelativeTime(editingMember.last_active_at)}</p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
          <Button onClick={onSave} className="rounded-lg" disabled={isUpdating}>
            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
